import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Home.css';

import config from '../config';
const API_URL = config.API_URL;

interface Stats {
    totalUsers: number;
    onlineUsers: number;
    internationalUsers: number;
}

const Home: React.FC = () => {
    const [stats, setStats] = useState<Stats>({ totalUsers: 0, onlineUsers: 0, internationalUsers: 0 });
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('token');
        setIsLoggedIn(!!token);

        // Fetch stats
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${API_URL}/stats`);
                if (response.data.success) {
                    setStats(response.data.stats);
                }
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            }
        };
        fetchStats();
    }, []);

    const handleGetStarted = () => {
        if (isLoggedIn) {
            navigate('/chat');
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="home">
            {/* Navbar */}
            <nav className="navbar">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/search" className="nav-link">Discover</Link>
                        <a href="#features" className="nav-link">Features</a>
                        <a href="#about" className="nav-link">About</a>
                        {isLoggedIn ? (
                            <>
                                <Link to="/profile" className="nav-link">Profile</Link>
                                <Link to="/chat" className="nav-btn primary">Start Chatting</Link>
                            </>
                        ) : (
                            <Link to="/login" className="nav-btn primary">Sign In</Link>
                        )}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <p className="hero-subtitle">Connecting Northeastern Students</p>
                    <h1 className="hero-title">
                        BUILD YOUR<br />
                        <span className="highlight">NETWORK</span>
                    </h1>
                    <p className="hero-description">
                        Meet fellow Huskies through random video chats. Break the ice, 
                        find study partners, discover clubs, and build lasting friendships 
                        — all within the Northeastern community.
                    </p>
                    <div className="hero-buttons">
                        <button onClick={handleGetStarted} className="btn-primary">
                            {isLoggedIn ? 'Start Chatting' : 'Get Started'}
                            <span className="btn-arrow">→</span>
                        </button>
                        <a href="#features" className="btn-secondary">
                            Learn More
                            <span className="btn-icon">↓</span>
                        </a>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="hero-image-container">
                        <div className="floating-card card-1">
                            <span className="card-emoji">🎓</span>
                            <span>Students Online</span>
                            <strong>{stats.onlineUsers}</strong>
                        </div>
                        <div className="floating-card card-2">
                            <span className="card-emoji">🌍</span>
                            <span>Countries</span>
                            <strong>50+</strong>
                        </div>
                        <div className="floating-card card-3">
                            <span className="card-emoji">💬</span>
                            <span>Connections Made</span>
                            <strong>{stats.totalUsers * 5}+</strong>
                        </div>
                        <div className="dot-pattern"></div>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="stats-bar">
                <div className="stat-item">
                    <span className="stat-number">{stats.totalUsers}+</span>
                    <span className="stat-label">Registered Students</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                    <span className="stat-number">{stats.onlineUsers}</span>
                    <span className="stat-label">Online Now</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                    <span className="stat-number">{stats.internationalUsers}+</span>
                    <span className="stat-label">International Students</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                    <span className="stat-number">100%</span>
                    <span className="stat-label">Verified .edu Emails</span>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features">
                <h2 className="section-title">How It Works</h2>
                <p className="section-subtitle">Connect with fellow Huskies in three simple steps</p>
                
                <div className="features-grid">
                    <div className="feature-card">
                        <div className="feature-icon">🔐</div>
                        <h3>Verify Your Identity</h3>
                        <p>Sign up with your Northeastern .edu email and student ID. Only verified Huskies can join.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">🎲</div>
                        <h3>Get Matched Randomly</h3>
                        <p>Click "Start Chat" and get paired with another online student. It's like Omegle, but safer!</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">💬</div>
                        <h3>Chat & Connect</h3>
                        <p>Video chat, text, or skip to meet someone new. Found a friend? Add them to stay connected.</p>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="about">
                <div className="about-content">
                    <h2>Why NU Connect?</h2>
                    <p>
                        Starting at a new university can be intimidating. Finding friends, 
                        study groups, or just someone to grab coffee with shouldn't be hard. 
                        NU Connect breaks down barriers by randomly connecting you with 
                        fellow Northeastern students.
                    </p>
                    <ul className="about-list">
                        <li>✓ Meet students from different majors and years</li>
                        <li>✓ Get insider tips about courses and professors</li>
                        <li>✓ Discover clubs and organizations</li>
                        <li>✓ Find study partners and project teammates</li>
                        <li>✓ Safe space — only verified .edu emails allowed</li>
                    </ul>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta">
                <h2>Ready to Meet Fellow Huskies?</h2>
                <p>Join hundreds of Northeastern students already connecting on NU Connect.</p>
                <button onClick={handleGetStarted} className="btn-primary large">
                    {isLoggedIn ? 'Go to Chat' : 'Join Now — It\'s Free'}
                </button>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <img src="/pngegg.png" alt="NU Connect" className="footer-logo" />
                        <span>NU Connect</span>
                    </div>
                    <div className="footer-links">
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/terms">Terms of Service</Link>
                        <a href="mailto:support@nuconnect.com">Contact</a>
                    </div>
                    <p className="footer-copy">© 2024 NU Connect. Made for Northeastern Students.</p>
                </div>
            </footer>
        </div>
    );
};

export default Home;